/**
 * Standard sport catalog for facility sport creation (AG-3 compliant config).
 * The catalog now uses the thin sport icon keys used by the admin sport picker.
 */

export interface SportCatalogEntry {
  name: string;
  icon: string;
  defaultDuration: number;
  defaultColor: string;
}

// Curated list of common sports with thin sport icon mappings.
export const SPORT_CATALOG: SportCatalogEntry[] = [
  { name: 'Badminton', icon: 'badminton', defaultDuration: 45, defaultColor: '#10B981' },
  { name: 'Baseball', icon: 'baseball', defaultDuration: 90, defaultColor: '#3B82F6' },
  { name: 'Basketball', icon: 'basketball', defaultDuration: 60, defaultColor: '#F97316' },
  { name: 'Biking', icon: 'biking', defaultDuration: 60, defaultColor: '#10B981' },
  { name: 'Bowling', icon: 'bowling', defaultDuration: 60, defaultColor: '#F97316' },
  { name: 'Boxing', icon: 'boxing', defaultDuration: 60, defaultColor: '#EF4444' },
  { name: 'Chess', icon: 'chess', defaultDuration: 60, defaultColor: '#8B5CF6' },
  { name: 'Cricket', icon: 'cricket', defaultDuration: 120, defaultColor: '#3B82F6' },
  { name: 'Field Hockey', icon: 'field-hockey', defaultDuration: 60, defaultColor: '#06B6D4' },
  { name: 'Fitness', icon: 'user-fast-running', defaultDuration: 60, defaultColor: '#EF4444' },
  { name: 'Football', icon: 'football', defaultDuration: 90, defaultColor: '#10B981' },
  { name: 'Golf', icon: 'golf', defaultDuration: 120, defaultColor: '#10B981' },
  { name: 'Padel', icon: 'paddle-tennis-racket', defaultDuration: 60, defaultColor: '#8B5CF6' },
  { name: 'Pickleball', icon: 'paddle-tennis-racket', defaultDuration: 45, defaultColor: '#06B6D4' },
  { name: 'Pool', icon: 'pool', defaultDuration: 60, defaultColor: '#3B82F6' },
  { name: 'Rugby', icon: 'rugby', defaultDuration: 90, defaultColor: '#EF4444' },
  { name: 'Snooker', icon: 'snooker', defaultDuration: 60, defaultColor: '#10B981' },
  { name: 'Squash', icon: 'paddle-tennis-racket', defaultDuration: 45, defaultColor: '#F59E0B' },
  { name: 'Swimming', icon: 'swimming', defaultDuration: 60, defaultColor: '#06B6D4' },
  { name: 'Table Tennis', icon: 'ping-pong', defaultDuration: 30, defaultColor: '#EF4444' },
  { name: 'Tennis', icon: 'tennis', defaultDuration: 60, defaultColor: '#10B981' },
  { name: 'Volleyball', icon: 'volleyball', defaultDuration: 60, defaultColor: '#F59E0B' },
  { name: 'Yoga', icon: 'yoga', defaultDuration: 60, defaultColor: '#EC4899' },
];

// Icons available for custom sport icon picker.
export const SPORT_ICON_OPTIONS = [
  'badminton',
  'baseball',
  'basketball',
  'biking',
  'bowling',
  'chess',
  'boxing',
  'cricket',
  'field-hockey',
  'football',
  'golf',
  'paddle-tennis-racket',
  'pickleball',
  'ping-pong',
  'pool',
  'rugby',
  'shuttlecock',
  'snooker',
  'swimming',
  'tennis',
  'user-fast-running',
  'volleyball',
  'yoga',
] as const;

export type SportIconName = (typeof SPORT_ICON_OPTIONS)[number];

/**
 * Get catalog entry by sport name (case-insensitive).
 */
export function getCatalogEntry(name: string): SportCatalogEntry | undefined {
  return SPORT_CATALOG.find((entry) => entry.name.toLowerCase() === name.toLowerCase());
}

/**
 * Get default icon for a sport name from catalog.
 * Returns undefined if not in catalog.
 */
export function getDefaultIcon(sportName: string): string | undefined {
  return getCatalogEntry(sportName)?.icon;
}

/**
 * Resolve the icon to display for a sport.
 * Priority: explicit icon → catalog default → fallback.
 */
export function resolveSportIcon(
  explicitIcon: string | null | undefined,
  sportName: string,
  fallback = 'Dumbbell'
): string {
  if (explicitIcon) return explicitIcon;
  const catalogIcon = getDefaultIcon(sportName);
  if (catalogIcon) return catalogIcon;
  return fallback;
}
